import React from "react";

import { format } from "date-fns";

import { ClientId } from "@fartherfinance/frontend/api/Types";

import formatPhoneNumber from "../ClientProfile/Forms/utils/formatPhoneNumber";
import HouseholdNameLink from "../Households/HouseholdsTable/HouseholdNameLink";
import { unknownBalanceValue } from "@src/constants/unknownBalanceValue";
import { ClientColumnDefinitions } from "@src/multiCustodian/hooks/Advisor/useTableRows";
import {
  Cell,
  Row,
  SophisticatedCell,
} from "@src/sharedComponents/Table/Types";

import ImpersonateClientButton from "./ImpersonateClientButton";
import { ClientWithAdvisorName, TableKeysType } from "./types";

const _HouseholdMembershipsList: React.FC<{
  householdMemberships: ClientWithAdvisorName["householdMemberships"];
}> = ({ householdMemberships }) => {
  if (!householdMemberships || householdMemberships.length === 0) {
    return <span>{unknownBalanceValue}</span>;
  }

  if (householdMemberships.length === 1) {
    const household = householdMemberships[0];
    return (
      <HouseholdNameLink
        householdId={household.householdId}
        householdName={household.householdName}
      />
    );
  }

  return (
    <span>
      {householdMemberships.map((household) => (
        <HouseholdNameLink
          key={household.householdId}
          householdId={household.householdId}
          householdName={household.householdName}
        />
      ))}
    </span>
  );
};

export const clientTableColumnDefs: ClientColumnDefinitions<TableKeysType> = {
  Name: {
    empty: { value: "No clients found" },
    displayData: (client: ClientWithAdvisorName) =>
      `${client.name.first} ${client.name.last}`,
  },
  // Household: {
  //   empty: { value: unknownBalanceValue },
  //   displayData: (client: ClientWithAdvisorName) =>
  //     ({
  //       value: (
  //         <HouseholdMembershipsList
  //           householdMemberships={client.householdMemberships}
  //         />
  //       ),
  //     } as SophisticatedCell),
  // },
  Email: {
    displayData: (client: ClientWithAdvisorName) => ({
      value: client.emailAddress,
      fullValue: client.emailAddress.toLowerCase(),
    }),
  },
  Mobile: {
    displayData: (client: ClientWithAdvisorName) =>
      formatPhoneNumber(client.phoneNumber ?? unknownBalanceValue),
  },
  ID: {
    displayData: (client: ClientWithAdvisorName) => client.clientId,
  },
  Advisor: {
    displayData: (client: ClientWithAdvisorName) =>
      `${client.advisorName.first} ${client.advisorName.last}`,
  },
  Created: {
    displayData: (client: ClientWithAdvisorName) => ({
      value: format(client.registeredTime, "M/d/yyyy"),
      fullValue: client.registeredTime.toISOString(),
    }),
  },
  Login: {
    displayData: (client: ClientWithAdvisorName) =>
      ({
        value: (
          <ImpersonateClientButton
            clientId={client.clientId}
            clientEmail={client.emailAddress}
            clientName={client.name}
          />
        ),
        hideUnlessHovering: true,
      } as SophisticatedCell),
  },
} as const;

export function createDisplayDataRow(
  client: ClientWithAdvisorName,
  openClientDetails: (clientId: ClientId) => void
): Row<TableKeysType, Cell> {
  const baseRow = {
    key: client.clientId,
    onClick: () => openClientDetails(client.clientId),
  };

  const columnData = Object.keys(clientTableColumnDefs).reduce((acc, key) => {
    const columnKey = key as keyof typeof clientTableColumnDefs;
    acc[key as TableKeysType] =
      clientTableColumnDefs[columnKey].displayData(client);

    return acc;
  }, {} as Record<TableKeysType, Cell | string>);

  return { ...baseRow, ...columnData } as Row<TableKeysType, Cell>;
}
